import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
	BarChart3,
	Building2,
	Check,
	ChevronDown,
	Loader2,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { auth } from "../../lib/api.ts";
import { notifyError } from "../../lib/toast.ts";
import { useAuthStore } from "../../stores/auth-store.ts";

/**
 * Header branch switcher for chain owners. Hidden unless the user belongs to
 * more than one branch. Switching re-mints a scoped session and hard-reloads
 * so every cached query re-fetches against the new clinic.
 */
export function BranchSwitcher() {
	const { t, i18n } = useTranslation("branches");
	const lang = i18n.language.startsWith("ar") ? "ar" : "en";
	const setSession = useAuthStore((s) => s.setSession);
	const currentClinicId = useAuthStore((s) => s.clinic?.id);
	const [open, setOpen] = useState(false);

	const branchesQuery = useQuery({
		queryKey: ["branches"],
		queryFn: () => auth.branches(),
		staleTime: 5 * 60 * 1000,
		retry: false,
	});
	const branches = branchesQuery.data ?? [];

	const mut = useMutation({
		mutationFn: (clinicId: string) => auth.switchBranch(clinicId),
		onSuccess: (data) => {
			setSession(data);
			// Hard reload to drop all clinic-scoped caches/state cleanly.
			window.location.href = "/dashboard";
		},
		onError: (e) => notifyError(e),
	});

	if (branches.length <= 1) return null;

	const current =
		branches.find((b) => b.clinic_id === currentClinicId) ?? branches[0];
	if (!current) return null;
	const nameOf = (n: { ar: string; en?: string }) =>
		lang === "ar" ? n.ar : n.en || n.ar;

	return (
		<div className="relative">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg text-sm text-foreground hover:bg-muted max-w-[180px]"
				aria-haspopup="menu"
				aria-expanded={open}
			>
				<Building2 className="size-4 text-brand-600 shrink-0" aria-hidden />
				<span className="truncate">{nameOf(current.name)}</span>
				<ChevronDown
					className="size-3.5 text-muted-foreground shrink-0"
					aria-hidden
				/>
			</button>

			{open && (
				<>
					{/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop; close is reachable via the explicit toggle button */}
					<div
						className="fixed inset-0 z-20"
						onClick={() => setOpen(false)}
						aria-hidden
					/>
					<div
						role="menu"
						className="absolute z-30 mt-1 end-0 w-60 rounded-xl border border-border bg-card shadow-lg py-1"
					>
						{branches.map((b) => {
							const active = b.clinic_id === currentClinicId;
							return (
								<button
									key={b.clinic_id}
									type="button"
									role="menuitem"
									disabled={active || mut.isPending}
									onClick={() => {
										setOpen(false);
										mut.mutate(b.clinic_id);
									}}
									className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-start hover:bg-muted disabled:cursor-default ${active ? "text-brand-700 dark:text-brand-300" : "text-foreground"}`}
								>
									<span className="flex size-5 items-center justify-center shrink-0">
										{active ? (
											<Check className="size-4" aria-hidden />
										) : mut.isPending && mut.variables === b.clinic_id ? (
											<Loader2 className="size-4 animate-spin" aria-hidden />
										) : null}
									</span>
									<span className="truncate">{nameOf(b.name)}</span>
								</button>
							);
						})}
						<div className="my-1 border-t border-border" />
						<Link
							to="/branches"
							onClick={() => setOpen(false)}
							className="w-full flex items-center gap-2 px-3 py-2 text-sm text-start text-muted-foreground hover:bg-muted"
						>
							<BarChart3 className="size-4 text-brand-600" aria-hidden />
							{t("view_report")}
						</Link>
					</div>
				</>
			)}
		</div>
	);
}
